import { useEffect, useRef, useState } from 'preact/hooks'
import { ClientAdmin, ErreurAdmin } from './api.ts'
import { SeriesPane } from './series.tsx'
import { dateLongue, libelleEtat } from './presentation.ts'
import type {
  ApercuAnnulation,
  CalendrierAdmin,
  DetailDemande,
  InvitationAdmin,
  ServiceAdmin,
} from './types.ts'

const monnaie = (cents: number) =>
  new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(
    cents / 100,
  )
const erreurLisible = (e: unknown) =>
  e instanceof Error ? e.message : 'Cette action n’a pas pu être terminée.'
export const jourLocal = (date: Date, fuseau: string) =>
  new Intl.DateTimeFormat('fr-CA', {
    timeZone: fuseau,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
export const decalerDate = (jour: string, jours: number) =>
  new Date(Date.parse(`${jour}T12:00:00Z`) + jours * 86400000)
    .toISOString()
    .slice(0, 10)
export function debutSemaine(jour: string): string {
  const index = (new Date(`${jour}T12:00:00Z`).getUTCDay() + 6) % 7
  return decalerDate(jour, -index)
}

export function CalendrierPane({
  client,
  services,
  fuseau,
  ouvrir,
  connexion,
  actualisation,
}: {
  client: ClientAdmin
  services: ServiceAdmin[]
  fuseau: string
  ouvrir(id: string): void
  connexion(): void
  actualisation: unknown
}) {
  const aujourdHui = jourLocal(new Date(), fuseau)
  const [jour, setJour] = useState(aujourdHui),
    [service, setService] = useState(''),
    [revision, setRevision] = useState(0)
  const [resultat, setResultat] = useState<CalendrierAdmin | null>(null),
    [erreur, setErreur] = useState(''),
    [charge, setCharge] = useState(true)
  const [ouvrirSerie, setOuvrirSerie] = useState(false)
  const lundi = debutSemaine(jour),
    dimanche = decalerDate(lundi, 6)
  useEffect(() => {
    const c = new AbortController()
    setCharge(true)
    setErreur('')
    void client
      .calendrier(lundi, dimanche, service, c.signal)
      .then((value) => {
        if (!c.signal.aborted) setResultat(value)
      })
      .catch((e) => {
        if (!c.signal.aborted) {
          if (e instanceof ErreurAdmin && e.statut === 401) connexion()
          else setErreur(erreurLisible(e))
        }
      })
      .finally(() => {
        if (!c.signal.aborted) setCharge(false)
      })
    return () => c.abort()
  }, [client, lundi, dimanche, service, revision, actualisation])
  const heure = (valeur: string) =>
    new Intl.DateTimeFormat('fr-CA', {
      timeZone: fuseau,
      timeStyle: 'short',
    }).format(new Date(valeur))
  const etats: Record<string, string> = {
    provisoire: 'Créneau retenu',
    en_attente: 'Approbation en attente',
    confirmee: 'Confirmée',
    bloquee: 'Indisponible',
  }
  return (
    <section class="admin__panneau">
      <div class="admin__section">
        <div class="admin__filtres">
          <div class="admin__champ">
            <label for="calendrier-date">Semaine du</label>
            <input
              id="calendrier-date"
              type="date"
              value={jour}
              onChange={(e) => {
                if (e.currentTarget.value) setJour(e.currentTarget.value)
              }}
            />
          </div>
          <div class="admin__champ">
            <label for="calendrier-service">Service</label>
            <select
              id="calendrier-service"
              value={service}
              onChange={(e) => setService(e.currentTarget.value)}
            >
              <option value="">Tous les services</option>
              {services.map((s) => (
                <option key={s.cle} value={s.cle}>
                  {s.titre ?? s.nom ?? s.cle}
                </option>
              ))}
            </select>
          </div>
          <div class="admin__actions">
            <button
              type="button"
              class="admin__secondaire"
              onClick={() => setJour(decalerDate(jour, -7))}
              aria-label="Semaine précédente"
            >
              ←
            </button>
            <button
              type="button"
              class="admin__secondaire"
              onClick={() => setJour(aujourdHui)}
            >
              Aujourd’hui
            </button>
            <button
              type="button"
              class="admin__secondaire"
              onClick={() => setJour(decalerDate(jour, 7))}
              aria-label="Semaine suivante"
            >
              →
            </button>
            <button
              type="button"
              class="admin__texte-bouton"
              onClick={() => setRevision((r) => r + 1)}
            >
              Actualiser
            </button>
            <a class="admin__secondaire" href={client.horaireCsvUrl(lundi, dimanche, service)} download>
              Télécharger l’horaire CSV
            </a>
            <button type="button" class="admin__secondaire" onClick={() => setOuvrirSerie((valeur) => !valeur)}>{ouvrirSerie ? 'Fermer la réservation hebdomadaire' : 'Créer une réservation hebdomadaire'}</button>
          </div>
        </div>
        <p class="admin__note">
          Heures de {fuseau}. Ouvrez une réservation pour consulter sa fiche.
        </p>
      </div>
      {ouvrirSerie ? <SeriesPane client={client} dateInitiale={jour} fuseau={fuseau} connexion={connexion} creee={(date) => { setJour(date); setRevision((r) => r + 1) }} /> : null}
      {erreur ? (
        <p class="admin__message admin__message--erreur" role="alert">
          {erreur}
        </p>
      ) : null}
      <div role="status" class="admin__calendrier-statut">
        {charge
          ? 'Chargement du calendrier…'
          : `${resultat?.evenements.length ?? 0} occupations cette semaine`}
      </div>
      {!charge && !erreur ? (
        <div class="admin__calendrier">
          {Array.from({ length: 7 }, (_, i) => decalerDate(lundi, i)).map(
            (date) => {
              const items =
                resultat?.evenements.filter(
                  (e) =>
                    jourLocal(new Date(e.debut_utc), fuseau) <= date &&
                    jourLocal(new Date(Date.parse(e.fin_utc) - 1), fuseau) >=
                      date,
                ) ?? []
              return (
                <section
                  key={date}
                  class={`admin__jour${date === aujourdHui ? ' admin__jour--aujourdhui' : ''}`}
                >
                  <h2>
                    <time dateTime={date}>
                      {new Intl.DateTimeFormat('fr-CA', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                      }).format(new Date(`${date}T12:00:00Z`))}
                    </time>
                  </h2>
                  {items.length ? (
                    items.map((e) => {
                      const contenu = (
                        <>
                          <strong>
                            {heure(e.debut_utc)} — {heure(e.fin_utc)}
                          </strong>
                          <span>{e.ressource ?? 'Ressource'}</span>
                          {e.demandeur ? <span>{e.demandeur}</span> : null}
                          <small>{etats[e.etat] ?? e.etat}</small>
                          {e.motif ? <small>{e.motif}</small> : null}
                        </>
                      )
                      return e.demande_id ? (
                        <button
                          class={`admin__evenement admin__evenement--${e.etat}`}
                          type="button"
                          key={e.id}
                          onClick={() => ouvrir(e.demande_id!)}
                        >
                          {contenu}
                        </button>
                      ) : (
                        <div class="admin__evenement" key={e.id}>
                          {contenu}
                        </div>
                      )
                    })
                  ) : (
                    <p class="admin__note">Aucune occupation</p>
                  )}
                </section>
              )
            },
          )}
        </div>
      ) : null}
    </section>
  )
}

export function InvitationsPane({
  client,
  service,
  fuseau,
  modifiable,
  connexion,
}: {
  client: ClientAdmin
  service: string
  fuseau: string
  modifiable: boolean
  connexion(): void
}) {
  const [invitations, setInvitations] = useState<InvitationAdmin[]>([])
  const [organisme, setOrganisme] = useState('')
  const [jours, setJours] = useState(7)
  const [maximum, setMaximum] = useState(1)
  const [nouveauLien, setNouveauLien] = useState('')
  const [message, setMessage] = useState('')
  const [erreur, setErreur] = useState('')
  const [mutation, setMutation] = useState(false)
  const [revision, setRevision] = useState(0)
  useEffect(() => setNouveauLien(''), [service])
  useEffect(() => {
    const controleur = new AbortController()
    void client.invitations(service, controleur.signal)
      .then((valeur) => { if (!controleur.signal.aborted) setInvitations(valeur.invitations) })
      .catch((cause) => {
        if (controleur.signal.aborted) return
        if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
        else setErreur(erreurLisible(cause))
      })
    return () => controleur.abort()
  }, [client, service, revision])
  const creer = async (e: Event) => {
    e.preventDefault()
    if (mutation || !organisme.trim() || jours < 1 || jours > 365 || maximum < 1 || maximum > 1000) return
    setMutation(true)
    setErreur('')
    setMessage('')
    try {
      const reponse = await client.creerInvitation(service, organisme.trim(), jours, maximum)
      setNouveauLien(reponse.invitation.url)
      setOrganisme('')
      setMessage('Lien créé. Copiez-le maintenant pour le transmettre à l’organisme; il ne sera plus affiché après avoir quitté cette page.')
      setRevision((v) => v + 1)
    } catch (cause) {
      if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
      else setErreur(erreurLisible(cause))
    } finally { setMutation(false) }
  }
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(nouveauLien)
      setMessage('Lien copié. Transmettez-le uniquement à l’organisme concerné.')
    } catch { setMessage('Sélectionnez et copiez le lien manuellement.') }
  }
  const revoquer = async (id: string) => {
    if (mutation) return
    setMutation(true)
    setErreur('')
    try {
      await client.revoquerInvitation(service, id)
      setNouveauLien('')
      setMessage('Invitation révoquée. Elle ne permet plus de commencer une demande.')
      setRevision((v) => v + 1)
    } catch (cause) {
      if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
      else setErreur(erreurLisible(cause))
    } finally { setMutation(false) }
  }
  return <section class="admin__section" aria-label="Invitations des organismes">
    <h2>Prêt sur invitation</h2>
    <p>Seuls les organismes qui reçoivent un lien valide peuvent commencer une demande. Un lien peut expirer, atteindre son nombre maximal de demandes ou être révoqué.</p>
    {modifiable ? <form onSubmit={creer} class="admin__filtres">
      <div class="admin__champ"><label for="invitation-organisme">Organisme invité</label><input id="invitation-organisme" required maxLength={200} value={organisme} onInput={(e) => setOrganisme(e.currentTarget.value)} /></div>
      <div class="admin__champ"><label for="invitation-jours">Validité en jours</label><input id="invitation-jours" type="number" min="1" max="365" required value={jours} onInput={(e) => setJours(Number(e.currentTarget.value))} /></div>
      <div class="admin__champ"><label for="invitation-maximum">Demandes permises</label><input id="invitation-maximum" type="number" min="1" max="1000" required value={maximum} onInput={(e) => setMaximum(Number(e.currentTarget.value))} /></div>
      <button class="admin__primaire" disabled={mutation || !organisme.trim()} type="submit">Créer un lien</button>
    </form> : null}
    {nouveauLien ? <div class="admin__section" role="status">
      <label for="invitation-lien">Lien à transmettre — affiché une seule fois</label>
      <div class="admin__actions"><input id="invitation-lien" readOnly value={nouveauLien} onFocus={(e) => e.currentTarget.select()} /><button class="admin__secondaire" type="button" onClick={() => void copier()}>Copier</button></div>
    </div> : null}
    {message ? <p role="status">{message}</p> : null}
    {erreur ? <p class="admin__message admin__message--erreur" role="alert">{erreur}</p> : null}
    <h3>Liens créés</h3>
    {invitations.length ? <div class="admin__table-wrap"><table class="admin__table"><thead><tr><th>Organisme</th><th>Expiration</th><th>Utilisations</th><th>État</th>{modifiable ? <th>Action</th> : null}</tr></thead><tbody>
      {invitations.map((i) => {
        const actif = !i.revoque_le_utc && Date.parse(i.expire_le_utc) > Date.now() && i.utilisations < i.maximum_demandes
        return <tr key={i.id}><td>{i.organisme}</td><td>{dateLongue(i.expire_le_utc, fuseau)}</td><td>{i.utilisations} / {i.maximum_demandes}</td><td>{i.revoque_le_utc ? 'Révoqué' : actif ? 'Actif' : 'Expiré ou épuisé'}</td>{modifiable ? <td>{!i.revoque_le_utc ? <button type="button" class="admin__danger" disabled={mutation} onClick={() => void revoquer(i.id)}>Révoquer</button> : null}</td> : null}</tr>
      })}
    </tbody></table></div> : <p class="admin__note">Aucune invitation créée.</p>}
  </section>
}

export function FicheDemande({
  id,
  client,
  fuseau,
  fermer,
  actualiser,
  connexion,
  gerer = true,
  revisionExterne,
  decider,
}: {
  id: string
  client: ClientAdmin
  fuseau: string
  fermer(): void
  actualiser(): void
  connexion(): void
  gerer?: boolean
  revisionExterne?: unknown
  decider?(id: string, type: 'approuver' | 'refuser', titre: string): void
}) {
  const dialogue = useRef<HTMLDialogElement>(null)
  const [detail, setDetail] = useState<DetailDemande | null>(null),
    [erreur, setErreur] = useState(''),
    [message, setMessage] = useState(''),
    [mutation, setMutation] = useState(false),
    [revision, setRevision] = useState(0)
  const [annulation, setAnnulation] = useState<ApercuAnnulation | null>(null),
    [motif, setMotif] = useState('')
  useEffect(() => {
    dialogue.current?.showModal()
    return () => dialogue.current?.close()
  }, [])
  useEffect(() => {
    const c = new AbortController()
    void client
      .detail(id, c.signal)
      .then((value) => {
        if (!c.signal.aborted) {
          setDetail(value)
          setErreur('')
        }
      })
      .catch((e) => {
        if (!c.signal.aborted) {
          if (e instanceof ErreurAdmin && e.statut === 401) connexion()
          else setErreur(erreurLisible(e))
        }
      })
    return () => c.abort()
  }, [client, id, revision, revisionExterne])
  const paiementEnCours = detail?.operations_paiement?.some(
    (o) => o.etat === 'a_faire' || o.etat === 'attente',
  )
  useEffect(() => {
    if (!paiementEnCours) return
    const minuterie = window.setInterval(
      () => setRevision((v) => v + 1),
      15_000,
    )
    return () => window.clearInterval(minuterie)
  }, [paiementEnCours])
  const gererErreur = (e: unknown) => {
    if (e instanceof ErreurAdmin && e.statut === 401) connexion()
    else setErreur(erreurLisible(e))
  }
  const preparerAnnulation = async () => {
    setMutation(true)
    setErreur('')
    try {
      setAnnulation(await client.apercuAnnulation(id))
    } catch (e) {
      gererErreur(e)
    } finally {
      setMutation(false)
    }
  }
  const annuler = async (event: Event) => {
    event.preventDefault()
    if (mutation || !annulation?.possible) return
    setMutation(true)
    setErreur('')
    try {
      await client.annuler(id, motif.trim())
      setAnnulation(null)
      setMessage(
        'La réservation a été annulée. Le traitement des remboursements prévus a été demandé.',
      )
      setRevision((v) => v + 1)
      actualiser()
    } catch (e) {
      gererErreur(e)
    } finally {
      setMutation(false)
    }
  }
  const envoyerContrat = async () => {
    if (mutation || !detail?.signature) return
    setMutation(true)
    setErreur('')
    try {
      const resultat = await client.envoyerContrat(id)
      setMessage(`Copie du contrat placée dans la boîte d’envoi pour ${resultat.destinataire}${resultat.simulation ? ' (simulation, aucun envoi réel)' : ''}.`)
      actualiser()
    } catch (e) { gererErreur(e) }
    finally { setMutation(false) }
  }
  const periode = detail?.periode ?? detail?.occupation
  const etatsPaiement: Record<string, string> = {
    a_payer: 'À payer',
    paye: 'Réglé',
    echoue: 'Échec',
    rembourse: 'Remboursé',
    libere: 'Retenue libérée',
  }
  return (
    <dialog
      ref={dialogue}
      class="admin__dialogue admin__fiche"
      aria-labelledby="fiche-titre"
      onCancel={fermer}
      onClose={fermer}
    >
      <div class="admin__section-entete">
        <h2 id="fiche-titre">Détail de la réservation</h2>
        <button
          class="admin__texte-bouton"
          type="button"
          disabled={mutation}
          onClick={() => setRevision((v) => v + 1)}
        >
          Actualiser
        </button>
        <button
          class="admin__secondaire"
          type="button"
          onClick={fermer}
          aria-label="Fermer la fiche"
        >
          Fermer
        </button>
      </div>
      <div class="admin__section">
        {erreur ? (
          <p class="admin__message admin__message--erreur" role="alert">
            {erreur}
          </p>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
        {!detail && !erreur ? <p role="status">Chargement…</p> : null}
        {detail ? (
          <>
            <h3>{detail.service.titre}</h3>
            <span class={`admin__etat admin__etat--${detail.demande.etat}`}>
              {libelleEtat(detail.demande.etat)}
            </span>
            <dl class="admin__renseignements">
              <dt>Créée le</dt>
              <dd>{dateLongue(detail.demande.cree_le_utc, fuseau)}</dd>
              {detail.ressource ? (
                <>
                  <dt>Ressource</dt>
                  <dd>{detail.ressource}</dd>
                </>
              ) : null}
              {periode ? (
                <>
                  <dt>Début</dt>
                  <dd>{dateLongue(periode.debut_utc, fuseau)}</dd>
                  <dt>Fin</dt>
                  <dd>{dateLongue(periode.fin_utc, fuseau)}</dd>
                </>
              ) : null}
              {detail.renseignements
                .filter((r) => r.valeur !== null && r.valeur !== '')
                .map((r, i) => (
                  <div class="admin__renseignement" key={i}>
                    <dt>{r.libelle}</dt>
                    <dd>
                      {typeof r.valeur === 'boolean'
                        ? r.valeur
                          ? 'Oui'
                          : 'Non'
                        : String(r.valeur)}
                    </dd>
                  </div>
                ))}
            </dl>
            {detail.pieces?.length ? (
              <section class="admin__carte">
                <h3>Pièces jointes</h3>
                <ul>
                  {detail.pieces.map((piece) => (
                    <li key={piece.id}>
                      <a href={`/api/admin/demandes/${encodeURIComponent(detail.demande.id)}/pieces/${encodeURIComponent(piece.id)}`}>
                        Télécharger {piece.nom}
                      </a>{' '}
                      <small>({Math.ceil(piece.taille / 1024)} Ko)</small>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {detail.paiements.length ? (
              <section>
                <h3>Paiements et dépôt</h3>
                {detail.paiements.map((p) => (
                  <div class="admin__ligne-paiement" key={p.id}>
                    <span>
                      {p.nature === 'depot' ? 'Dépôt' : 'Tarif'}
                      {p.fournisseur === 'simule' ? ' (simulation)' : ''}
                    </span>
                    <strong>{monnaie(p.montant_cents)}</strong>
                    <span>{etatsPaiement[p.etat] ?? p.etat}</span>
                    {p.rembourse_cents ? (
                      <small>Remboursé : {monnaie(p.rembourse_cents)}</small>
                    ) : null}
                  </div>
                ))}
              </section>
            ) : null}
            {detail.operations_paiement?.some((o) => o.nature !== 'expirer') ? (
              <section class="admin__suivi-paiements">
                <h3>Suivi des remboursements</h3>
                {detail.operations_paiement
                  .filter((o) => o.nature !== 'expirer')
                  .map((o) => (
                    <div
                      key={o.id}
                      class={`admin__operation-paiement${o.etat === 'bloquee' ? ' admin__message--erreur' : ''}`}
                    >
                      <strong>
                        {o.nature === 'liberer'
                          ? 'Libération de la retenue'
                          : 'Remboursement'}{' '}
                        · {monnaie(o.montant_cents)}
                      </strong>
                      <p>{o.message}</p>
                    </div>
                  ))}
              </section>
            ) : null}
            {detail.signature ? (
              <section class="admin__carte">
                <h3>Contrat signé</h3>
                <p>
                  {detail.signature.nom_tape} ·{' '}
                  {dateLongue(detail.signature.signe_le_utc, fuseau)}
                </p>
                <a href={detail.signature.url_pdf}>
                  Télécharger le contrat PDF
                </a>
                {gerer ? <button type="button" class="admin__secondaire" disabled={mutation} onClick={() => void envoyerContrat()}>
                  Envoyer une copie par courriel
                </button> : null}
              </section>
            ) : null}
            {gerer &&
            detail.demande.etat === 'en_attente_approbation' &&
            decider ? (
              <section class="ux-decision-fiche">
                <div>
                  <h3>Cette demande attend votre réponse</h3>
                  <p>
                    Vérifiez les renseignements, puis approuvez ou expliquez
                    votre refus.
                  </p>
                </div>
                <div class="admin__actions">
                  <button
                    class="admin__secondaire"
                    type="button"
                    onClick={() => decider(id, 'refuser', detail.service.titre)}
                  >
                    Refuser
                  </button>
                  <button
                    class="admin__primaire"
                    type="button"
                    onClick={() =>
                      decider(id, 'approuver', detail.service.titre)
                    }
                  >
                    Approuver la réservation
                  </button>
                </div>
              </section>
            ) : null}
            {annulation ? (
              <form class="admin__annulation" onSubmit={annuler}>
                <h3>Vérifier l’annulation</h3>
                <p>{annulation.message}</p>
                {annulation.possible ? (
                  <>
                    <dl class="admin__renseignements">
                      <dt>Remboursement prévu</dt>
                      <dd>
                        {monnaie(
                          annulation.remboursements.tarif_cents +
                            annulation.remboursements.depot_cents,
                        )}
                      </dd>
                      <dt>Montant retenu</dt>
                      <dd>
                        {monnaie(
                          annulation.retenues.tarif_cents +
                            annulation.retenues.depot_cents,
                        )}
                      </dd>
                    </dl>
                    {annulation.politique.length ? (
                      <ul>
                        {annulation.politique.map((texte) => (
                          <li>{texte}</li>
                        ))}
                      </ul>
                    ) : null}
                    <div class="admin__champ">
                      <label for="motif-annulation">
                        Motif de l’annulation
                      </label>
                      <textarea
                        id="motif-annulation"
                        value={motif}
                        onInput={(e) => setMotif(e.currentTarget.value)}
                        required
                      />
                    </div>
                    <p>Le créneau sera libéré. Cette action est définitive.</p>
                  </>
                ) : null}
                <div class="admin__actions">
                  <button
                    type="button"
                    class="admin__secondaire"
                    disabled={mutation}
                    onClick={() => setAnnulation(null)}
                  >
                    Conserver la réservation
                  </button>
                  {annulation.possible ? (
                    <button
                      type="submit"
                      class="admin__danger"
                      disabled={mutation || !motif.trim()}
                    >
                      {mutation ? 'Annulation…' : 'Confirmer l’annulation'}
                    </button>
                  ) : null}
                </div>
              </form>
            ) : (
              <div class="admin__actions admin__fiche-actions">
                {gerer && detail.url_reprise ? (
                  <a href={detail.url_reprise} target="_blank" rel="noreferrer">
                    Ouvrir le parcours citoyen
                  </a>
                ) : null}
                {gerer && detail.annulation.possible ? (
                  <button
                    type="button"
                    class="admin__danger"
                    disabled={mutation}
                    onClick={() => void preparerAnnulation()}
                  >
                    Annuler la réservation
                  </button>
                ) : null}
              </div>
            )}
            <details class="admin__journal">
              <summary>
                Historique de la réservation ({detail.evenements.length})
              </summary>
              <ol>
                {detail.evenements.map((e, i) => (
                  <li key={i}>
                    <time dateTime={e.le_utc}>
                      {dateLongue(e.le_utc, fuseau)}
                    </time>{' '}
                    — {e.nom.replaceAll('_', ' ')}
                  </li>
                ))}
              </ol>
            </details>
          </>
        ) : null}
      </div>
    </dialog>
  )
}

export function ImporterService({
  client,
  termine,
  connexion,
}: {
  client: ClientAdmin
  termine(cle: string): void
  connexion(): void
}) {
  const [document, setDocument] = useState<unknown>(null),
    [nom, setNom] = useState(''),
    [erreur, setErreur] = useState(''),
    [envoi, setEnvoi] = useState(false)
  const choisir = async (file: File | undefined) => {
    setDocument(null)
    setNom('')
    setErreur('')
    if (!file) return
    if (file.size > 1024 * 1024) {
      setErreur('Choisissez un fichier de moins de 1 Mo.')
      return
    }
    try {
      const value: unknown = JSON.parse(await file.text())
      if (!value || typeof value !== 'object' || !('service' in value))
        throw Error()
      setDocument(value)
      setNom(file.name)
    } catch {
      setErreur(
        'Ce fichier ne contient pas une configuration JSON de service valide.',
      )
    }
  }
  const importer = async (event: Event) => {
    event.preventDefault()
    if (!document || envoi) return
    setEnvoi(true)
    setErreur('')
    try {
      const reponse = (await client.importer(document)) as {
        service_cle: string
      }
      setDocument(null)
      setNom('')
      termine(reponse.service_cle)
    } catch (e) {
      if (e instanceof ErreurAdmin && e.statut === 401) connexion()
      else setErreur(erreurLisible(e))
    } finally {
      setEnvoi(false)
    }
  }
  return (
    <details class="admin__import">
      <summary>Reprendre un service existant</summary>
      <form onSubmit={importer}>
        <p>
          Importez le fichier de service transmis par votre équipe. Un brouillon
          sera préparé pour vérification avant sa publication.
        </p>
        <div class="admin__champ">
          <label for="fichier-service">Fichier du service (.json)</label>
          <input
            id="fichier-service"
            type="file"
            accept=".json,application/json"
            onChange={(e) => void choisir(e.currentTarget.files?.[0])}
          />
        </div>
        {nom ? <p>{nom} est prêt à être vérifié.</p> : null}
        {erreur ? (
          <p role="alert" class="admin__message admin__message--erreur">
            {erreur}
          </p>
        ) : null}
        <button
          type="submit"
          class="admin__secondaire"
          disabled={!document || envoi}
        >
          {envoi ? 'Vérification…' : 'Préparer le brouillon'}
        </button>
      </form>
    </details>
  )
}
